/* React Components */
import React, { useState , useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
/* Providers */
import { useAuth } from '../context/AuthContext';
/* Components */
import { supabase } from '../services/Supabase';
import commonFunction from "../utils/CommonFunction";
/* React Icons */
import { RiUser3Line } from "react-icons/ri";
import { BiPowerOff } from "react-icons/bi";
import { TbMessage2 } from "react-icons/tb";
import { BsBrightnessHigh, BsMoon } from "react-icons/bs";
import { LuLogOut, LuPaintbrush } from "react-icons/lu";
import { FaSpinner } from 'react-icons/fa';
import { FaFilePen } from 'react-icons/fa6';
import { AiOutlineDoubleRight, AiOutlineDoubleLeft  } from "react-icons/ai";
import { HiMiniChatBubbleOvalLeft } from "react-icons/hi2";

/* Styles and Images */
import CompanyLogoWhite from "../assets/img/m-comp-logo-white.png";
import CompanyLogoDark from "../assets/img/m-comp-logo-black.png";

export default function SideBar() {
    /* User Information */
    const { user, logOut, userMetaData, setUserMetaData, setUserTheme, userTheme, categoryLists, setLoadingCatMsg, hasARIAccess, myKajabiPlans } = useAuth();
    const userId = user?.id ?? '';

    /* Providers */ 
    const navigate = useNavigate();
    const location = useLocation();
    const { parseError, isRouteActive } = commonFunction();

    /* FORMS: Use States / Setters */
    const [isMobile, setIsMobile] = useState(false);
    const [loadingButton, setLoadingButton] = useState(false);
    const [showCatSidebar, setShowCatSidebar] = useState(true);

    /** Profile Settings Form */
    const [showProfileSettings, setShowProfileSettings] = useState(false);
    const [formData, setFormData] = useState({
        first_name: userMetaData?.first_name || '',
        last_name: userMetaData?.last_name || '',
        nickname: userMetaData?.nickname || '',
        email: user?.email || '',
        password: "",
        confirm_password: ""
    });
    const { first_name, last_name, nickname, email, password, confirm_password } = formData;
    
    const companyLogo = (userTheme==='dark-mode') ? CompanyLogoWhite : CompanyLogoDark;

    useEffect(() => {

        // Function to check screen size
        const checkScreenSize = () => {
          setIsMobile(window.matchMedia('(max-width: 768px)').matches);
        };
    
        // Initial check when component mounts
        checkScreenSize();
    
        // Add event listener to detect screen size changes
        window.addEventListener('resize', checkScreenSize);
    
        // Clean up the event listener when the component unmounts
        return () => {
           window.removeEventListener('resize', checkScreenSize);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* Functions */
    const showChatHistory = async () => {
        navigate("/dashboard", {
            replace: true,
            state: { from: location }
        });
    };

    const onChange = (e) => {
        setFormData((prevState) => ({
            ...prevState,
            [e.target.id]: e.target.value,
        }));
    }

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoadingButton(true);
        if (password && password !== confirm_password) {
            toast.error("Password should match confirm password");
            setLoadingButton(false);
            return;
        }
        if (password) {
            const { data, error } = await supabase.auth.updateUser({
                password: password
            })
            console.log('Password updated', data, error);
        }

        const { data: users_metadata, error: metaError } = await supabase
        .from('users_metadata')
            .update({
                user_email: email,
                first_name,
                last_name,
                nickname,
                user_theme: userTheme
            })
            .eq('user_id', userId)
        .select().maybeSingle();
    
        if (metaError) {
            toast.error("Metadata update error: " + metaError.message);
        }
        setUserMetaData(users_metadata);

        updateChatProfileName(nickname ?? first_name);
        setShowProfileSettings(false);

        setFormData(prev => ({
            ...prev,
            password: '',
            confirm_password: ''
        }));

        toast.success("Profile updated successfully.");
        
        setLoadingButton(false);
    }

    const updateChatProfileName = (name) =>{
        const elements = document.querySelectorAll('.chatMsgName');
        elements.forEach(element => {
            element.textContent = name;
        });
    }

    const showSidebar = () => {
        setShowCatSidebar(prev => !prev);
    };

    // Toggle dark/light
    const updateTheme = async (newTheme) => {
        const { data, error } = await supabase
            .from("users_metadata")
            .update({ user_theme: newTheme })
            .eq("user_id", userId)
            .select()
            .maybeSingle();

        if (error) {
            const errorObject = parseError(error.message);
            toast.error(`Problem changing theme (${errorObject.code})`);
            return;
        }
        setUserMetaData(data);
    };

    const toggleTheme = () => {
        const newTheme = (userTheme === "dark-mode") ? "light-mode" : "dark-mode";
        updateTheme(newTheme);
        setUserTheme(newTheme);
    };

    const setAriTheme = () => {
        setUserTheme("ari-theme");
        updateTheme("ari-theme");
    };

    const handleCategoryClick = (category) => {
        setLoadingCatMsg(true);
        navigate(`/category/${category.slug}`);
    };

    const dailyRoutineChat = () => {
        navigate("/dashboard", {
            replace: true,
            state: { from: location }
        });
    };

    const missionSupportCategory = categoryLists.find((category) =>
        category.category_name?.toLowerCase() === "mission support"
    );
    const remainingCategoryLists = categoryLists
        .filter((category) => category.category_name?.toLowerCase() !== "mission support")
        .sort((a, b) => (a.category_name || "").localeCompare(b.category_name || ""));

    return (
        <aside>

            {/* Icon Sidebar (Thin) */}
            <div className="sidebar-menu-icons">
                <ul className='topbar-icons'>
                    <li className="mb-30">
                        <Link to="/">
                            <div className="sidebar-logo"><img src={(userTheme==='ari-theme') ? CompanyLogoWhite : companyLogo} alt="Company Logo" /></div>
                        </Link>
                    </li>
                    <li>
                        <button onClick={showChatHistory} className={`nav-link-button ${isRouteActive("/dashboard") ? "active" : ""}`}>
                            <TbMessage2 />
                            <span><i className="pointer"></i> Chats</span>
                        </button>
                    </li>
                    <li>
                        <Link onClick={()=> {setShowProfileSettings(!showProfileSettings)}}>
                            <RiUser3Line />
                            <span><i className="pointer"></i> Profile Settings</span>
                        </Link>
                    </li>
                    {/* Minimize Sidebar */}
                    {(hasARIAccess) && (
                        <li>
                            <Link onClick={showSidebar}>
                                {showCatSidebar ? (
                                    <>
                                        {isMobile ? (
                                            <>
                                                <AiOutlineDoubleRight />
                                                <span><i className="pointer"></i> Show Sidebar</span>
                                            </>
                                        ) : (
                                            <>
                                                <AiOutlineDoubleLeft />
                                                <span><i className="pointer"></i> Minimize Sidebar</span>
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {!isMobile ? (
                                            <>
                                                <AiOutlineDoubleRight />
                                                <span><i className="pointer"></i> Show Sidebar</span>
                                            </>
                                        ) : (
                                            <>
                                                <AiOutlineDoubleLeft />
                                                <span><i className="pointer"></i> Minimize Sidebar</span>
                                            </>
                                        )}
                                    </>
                                )}
                            </Link>
                        </li>
                    )}
                </ul>

                {/* Bottom Icons */}
                <ul className='bottom-icons'>
                    <li>
                        <Link onClick={setAriTheme}>
                            <LuPaintbrush />
                            <span><i className="pointer"></i> ARI Theme</span>
                        </Link>
                    </li>

                    <li>
                        <Link onClick={toggleTheme}>
                            {(userTheme === "dark-mode" || userTheme === "ari-theme") ? (
                            <>
                                <BsMoon />
                                <span><i className="pointer"></i> Dark Mode</span>
                            </>
                            ) : (
                            <>
                                <BsBrightnessHigh />
                                <span><i className="pointer"></i> Light Mode</span>
                            </>
                            )}
                        </Link>
                    </li>
                    <li>
                        <Link onClick={() => logOut()}>
                            <BiPowerOff />
                            <span><i className="pointer"></i> Logout</span>
                        </Link>
                    </li>
                </ul>
            </div>
            
            {/* Profile Settings */}
            <div className={`sidebar-profile-settings-container ${(showProfileSettings) ? 'show-container' : ''}`} onClick={()=> {setShowProfileSettings(!showProfileSettings)}}>
                <form onSubmit={onSubmit} method='POST' className={`p-settings-wrapper ${(showProfileSettings) ? 'slide-container' : ''}`} onClick={(e) => e.stopPropagation()}>
                    <div className="p-settings-top">
                        <div className="p-settings-header">
                            <h2>Profile Settings</h2>
                            <button className="mbtn btn-theme-default" type='button' onClick={() => logOut()}><LuLogOut /> Logout</button>
                        </div>
                        <div className="p-settings-body">
                            {/* Current Plan */}
                            <div className="form-wrapper">
                                <h4 className='currentPlanTxt'>Current Plan: <strong>{myKajabiPlans}</strong></h4>
                            </div>
                            {/* First Name */}
                            <div className="form-wrapper">
                                <label htmlFor="first_name">First Name *</label>
                                <div className="form-group">
                                    <input type="first_name" className="form-input" id="first_name" name="first_name" placeholder="First Name" value={first_name} onChange={onChange} required />
                                </div>
                            </div>
                            {/* Last Name */}
                            <div className="form-wrapper">
                                <label htmlFor="last_name">Last Name *</label>
                                <div className="form-group">
                                    <input type="last_name" className="form-input" id="last_name" name="last_name" placeholder="Last Name" value={last_name} onChange={onChange} required />
                                </div>
                            </div>
                            {/* Nickname */}
                            <div className="form-wrapper">
                                <label htmlFor="nickname">Nickname *</label>
                                <div className="form-group">
                                    <input type="nickname" className="form-input" id="nickname" name="nickname" placeholder="Nickname" value={nickname} onChange={onChange} required />
                                </div>
                            </div>
                            {/* Email */}
                            <div className="form-wrapper">
                                <label htmlFor="email">Email Address (Unchangeable)</label>
                                <div className="form-group">
                                    <input type="email" className="form-input" id="email" name="email" placeholder="Email Address" value={email} disabled={true} />
                                </div>
                            </div>
                            {/* Password */}
                            <div className="form-wrapper">
                                <label htmlFor="password">Password</label>
                                <div className="form-group">
                                    <input type="password" className="form-input" id="password" name="password" placeholder="Password" value={password} onChange={onChange} />
                                </div>
                            </div>
                            {/* Confirm Password */}
                            <div className="form-wrapper">
                                <label htmlFor="confirm_password">Confirm Password</label>
                                <div className="form-group">
                                    <input type="password" className="form-input" id="confirm_password" name="confirm_password" placeholder="Confirm Password" value={confirm_password} onChange={onChange} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="p-settings-bottom">
                        <div className="psettings-left">
                            <Link onClick={()=> {setShowProfileSettings(!showProfileSettings)}}>
                                <AiOutlineDoubleLeft />
                            </Link>
                        </div>
                        <div className="psettings-right">
                            <button className="mbtn btn-default" type="button" onClick={()=> {setShowProfileSettings(!showProfileSettings)}}>Cancel</button>
                            {loadingButton ? 
                                (<button className="mbtn btn-theme-default" type="button"><FaSpinner className="spinner" disabled /> Saving... </button>) : 
                                (<button className="mbtn btn-theme-default" type="submit">Save</button>)
                            }
                        </div>
                    </div>
                </form>
            </div>

            {/* ======================================================================= */}
            {/* Sidebar Coaching Lists (Wide) */}
            {(hasARIAccess) && (
                <div className={showCatSidebar ? 'sidebar-menu-lists' : 'sidebar-menu-lists hide-sidebar'}>
                    <div className="side-menu-list-heading">
                        <h1>Types of Coaching for Military Veterans and Their Families</h1>
                    </div>
                    <ul className='chat-history-display mm-category-lists'>
                        <li className="chat-hist-item-date">Coaching can be a transformative experience for military veterans and their families, offering support in various aspects of business and life. Here are some types of coaching that can positively impact them:</li>
                        {missionSupportCategory && (
                            <li className="chat-hist-item">
                                <button
                                    onClick={() => handleCategoryClick(missionSupportCategory)}
                                    className={
                                        location.pathname === `/category/${missionSupportCategory.slug}`
                                            ? "chat-link-btn active"
                                            : "chat-link-btn"
                                    }
                                    title={missionSupportCategory.category_name}
                                >
                                    <div className="ari-side-lists-title">
                                        <HiMiniChatBubbleOvalLeft />
                                        <b className="chat-hist-item-txt">{missionSupportCategory.category_name}</b>
                                    </div>
                                </button>
                            </li>
                        )}
                        <li className="chat-hist-item">
                            <span onClick={dailyRoutineChat} className={isRouteActive("/dashboard") ? "active" : ""}>
                                <div className="ari-side-lists-title">
                                    <FaFilePen /> <b className="chat-hist-item-txt">Daily Routine</b>
                                </div>
                            </span>
                        </li>

                        <li className="chat-hist-item-date"> Coaching Categories: </li>
                        {remainingCategoryLists.map((selCategory) => {
                            const isActive =
                                location.pathname === `/category/${selCategory.slug}`;

                            return (
                                <li className="chat-hist-item" key={selCategory.id}>
                                    <button
                                        onClick={() => handleCategoryClick(selCategory)}
                                        className={
                                        isActive
                                            ? "chat-link-btn active"
                                            : "chat-link-btn"
                                        }
                                        title={selCategory.category_name}
                                    >
                                        <div className="ari-side-lists-title">
                                            <HiMiniChatBubbleOvalLeft />
                                            <b className="chat-hist-item-txt">
                                                {selCategory.category_name}
                                            </b>
                                        </div>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </aside>
    )
}
